import { useLayoutEffect, useRef } from 'react';
import {
  isMainScrollRestored,
  mainScrollKey,
  restoreMainScroll,
  saveMainScroll,
} from '@/lib/mainScrollRestore';

const RESTORE_WINDOW_MS = 2500;

/**
 * Bind to `#app-main-scroll`. Saves on scroll + unmount, restores on (re)visit.
 * Keeps retrying via ResizeObserver until the page is tall enough (dashboard
 * loading shell → real content) so we do not land at the top.
 */
export function useMainScrollRestore(pathname: string, search = '') {
  const ref = useRef<HTMLElement | null>(null);
  const key = mainScrollKey(pathname, search);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    let settled = isMainScrollRestored(el, key);
    const tryRestore = () => {
      restoreMainScroll(el, key);
      settled = isMainScrollRestored(el, key);
      return settled;
    };
    tryRestore();

    const ro = new ResizeObserver(() => {
      if (!settled) tryRestore();
    });
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);

    const raf = requestAnimationFrame(() => tryRestore());
    const giveUp = window.setTimeout(() => {
      settled = true;
      ro.disconnect();
    }, RESTORE_WINDOW_MS);

    const onScroll = () => saveMainScroll(key, el.scrollTop, el);
    el.addEventListener('scroll', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(giveUp);
      ro.disconnect();
      el.removeEventListener('scroll', onScroll);
      saveMainScroll(key, el.scrollTop, el);
    };
  }, [key]);

  return ref;
}
